import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Container,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Alert,
  Snackbar,
  Chip,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Checkbox
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  OpenInNew as OpenInNewIcon,
  ViewColumn as ViewColumnIcon
} from '@mui/icons-material';



function BookingTableEnhanced() {
  const navigate = useNavigate();
  const location = useLocation();
  // Vorauswahl aus URL (z. B. Storno-Link aus Vertrags-Löschsperre): ?buchung_ids=1,2,3
  const [idVorauswahl, setIdVorauswahl] = useState(() => {
    const raw = new URLSearchParams(window.location.search).get('buchung_ids');
    if (!raw) return null;
    const ids = raw.split(',').map((s) => parseInt(s, 10)).filter((n) => Number.isInteger(n) && n > 0);
    return ids.length > 0 ? ids : null;
  });
  const [bookings, setBookings] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const pageSize = 200;
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [konten, setKonten] = useState([]);
  const [liquiditaetskonten, setLiquiditaetskonten] = useState([]);
  const [objekte, setObjekte] = useState([]);
  const [wohneinheiten, setWohneinheiten] = useState([]);
  const [forderungen, setForderungen] = useState([]);
  
  // Filter States
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState({
    year: '',
    objektid: '',
    buchungskontoid: '',
    buchungsart: '',
    wohnungsid: '',
    umsatzsteuersatz: '', // 🆕 NEU: USt-Satz-Filter
    belegstatus: '',
    zuordnungsstatus: '',
    betragVon: '', // 🆕 NEU: Betragsbereich-Filter
    betragBis: ''  // 🆕 NEU: Betragsbereich-Filter
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // 🆕 NEU: Spalten-Sichtbarkeit
  const [columnVisibility, setColumnVisibility] = useState({
    basic: true,
    objekt: true,
    steuer: false,
    extended: false,
    meta: true
  });
  
  // Dialog States
  const [editDialog, setEditDialog] = useState({ open: false, buchung: null });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    buchung: null,
    bewegungsid: null,
    stornoAlleSplits: false,
    relatedActiveCount: 1,
    loadingContext: false
  });
  const [zuordnungsDialog, setZuordnungsDialog] = useState({ open: false, buchung: null, neueForderungId: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedBuchungsIds, setSelectedBuchungsIds] = useState(new Set());
  const [batchStornoDialog, setBatchStornoDialog] = useState({ open: false });
  const [correctionDetails] = useState({ loading: false, data: null });

  // Edit Form State
  const [editForm, setEditForm] = useState({
    buchungskontoid: '',
    liquiditaetskonto_id: '',
    buchungsbetrag: '',
    buchungsdatum: '',
    notiz: '',
    objektid: '',
    wohnungsid: '',
    belegid: '',
    status: '',
    source_typ: '',
    source_id: ''
  });

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [kontenResponse, liquiditaetskontenResponse, objekte, wohneinheiten] = await Promise.all([
          apiService.getSachkonten(),
          apiService.getLiquiditaetskonten(),
          apiService.get('/api/objects'),
          apiService.get('/api/wohneinheiten')
        ]);
        
        setKonten(kontenResponse.konten || []);
        setLiquiditaetskonten(liquiditaetskontenResponse.konten || []);
        setObjekte(objekte || []);
        setWohneinheiten(wohneinheiten || []);
        
        try {
          const forderungenRes = await apiService.get('/api/forderungen-v2');
          setForderungen(Array.isArray(forderungenRes) ? forderungenRes : forderungenRes?.rows || []);
        } catch (err) {
          console.warn('Forderungen konnten nicht geladen werden:', err);
        }      } catch (error) {
        console.error('Fehler beim Laden der Stammdaten:', error);
        setSnackbar({ open: true, message: 'Fehler beim Laden der Stammdaten', severity: 'error' });
      }
    };

    fetchMeta();
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 400);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  const fetchBookings = useCallback(async ({ reset = false, nextPage = 0 } = {}) => {
    try {
      setIsLoading(true);
      const params = {
        limit: pageSize,
        offset: nextPage * pageSize,
      };

      if (debouncedSearchTerm) {
        params.q = debouncedSearchTerm;
      }

      if (idVorauswahl) {
        params.buchungsids = idVorauswahl.join(',');
      }

      const data = await apiService.get('/api/buchung', { params });
      const rows = Array.isArray(data?.rows) ? data.rows : Array.isArray(data) ? data : [];
      const total = typeof data?.totalCount === 'number' ? data.totalCount : null;

      setTotalCount(total);
      setAllBookings((prev) => (reset ? rows : [...prev, ...rows]));
      if (reset) setSelectedBuchungsIds(new Set(idVorauswahl || []));
      setHasMore(total !== null ? (nextPage + 1) * pageSize < total : rows.length === pageSize);
      setPage(nextPage);
    } catch (error) {
      console.error('Fehler beim Laden der Buchungen:', error);
      setSnackbar({ open: true, message: 'Fehler beim Laden der Buchungen', severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearchTerm, idVorauswahl]);

  useEffect(() => {
    fetchBookings({ reset: true, nextPage: 0 });
  }, [fetchBookings]);

  // 🆕 NEU: Erweiterte Filter-Funktionen
  const applyFilters = useCallback((data) => {
    let filtered = [...data];

    if (filters.year && filters.year !== '') {
      filtered = filtered.filter(booking => 
        new Date(booking.buchungsdatum).getFullYear() === parseInt(filters.year)
      );
    }

    if (filters.objektid && filters.objektid !== '') {
      filtered = filtered.filter(booking => booking.objektid === filters.objektid);
    }

    if (filters.buchungskontoid && filters.buchungskontoid !== '') {
      filtered = filtered.filter(booking => booking.buchungskontoid === filters.buchungskontoid);
    }

    if (filters.wohnungsid && filters.wohnungsid !== '') {
      filtered = filtered.filter(booking => booking.wohnungsid === filters.wohnungsid);
    }

    if (filters.belegstatus === 'mit_beleg') {
      filtered = filtered.filter(booking => booking.belegid !== null);
    } else if (filters.belegstatus === 'ohne_beleg') {
      filtered = filtered.filter(booking => booking.belegid === null);
    }

    if (filters.zuordnungsstatus === 'vollstaendig') {
      filtered = filtered.filter(booking => (booking.anzahl_zuordnungen || 0) > 0);
    } else if (filters.zuordnungsstatus === 'unzugeordnet') {
      filtered = filtered.filter(booking => (booking.anzahl_zuordnungen || 0) === 0);
    }

    // 🆕 NEU: USt-Satz-Filter
    if (filters.umsatzsteuersatz && filters.umsatzsteuersatz !== '') {
      filtered = filtered.filter(booking => 
        parseFloat(booking.umsatzsteuersatz || 0) === parseFloat(filters.umsatzsteuersatz)
      );
    }

    // 🆕 NEU: Betragsbereich-Filter (Von)
    if (filters.betragVon && filters.betragVon !== '') {
      filtered = filtered.filter(booking => 
        parseFloat(booking.buchungsbetrag) >= parseFloat(filters.betragVon)
      );
    }

    // 🆕 NEU: Betragsbereich-Filter (Bis)
    if (filters.betragBis && filters.betragBis !== '') {
      filtered = filtered.filter(booking => 
        parseFloat(booking.buchungsbetrag) <= parseFloat(filters.betragBis)
      );
    }

    return filtered;
  }, [filters]);

  // Filter anwenden
  useEffect(() => {
    if (showFilters) {
      const filtered = applyFilters(allBookings);
      setBookings(filtered);
    } else {
      setBookings(allBookings);
    }
  }, [allBookings, applyFilters, showFilters]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const detectBewegungsId = (buchung) => {
    const idAusZuordnung = Array.isArray(buchung?.zugeordnete_bewegungsids)
      ? buchung.zugeordnete_bewegungsids
          .map((id) => parseInt(id, 10))
          .find((id) => Number.isInteger(id) && id > 0)
      : null;

    const idAusSource = buchung?.source_typ === 'KONTOBEWEGUNG'
      ? parseInt(buchung?.source_id, 10)
      : null;

    const idAusNotiz = (() => {
      const match = String(buchung?.notiz || '').match(/Bewegungsid:\s*(\d+)/i);
      return match ? parseInt(match[1], 10) : null;
    })();

    return [idAusZuordnung, idAusSource, idAusNotiz]
      .find((id) => Number.isInteger(id) && id > 0) || null;
  };

  const hasCriticalEditChanges = (original, form) => {
    if (!original || !form) return false;

    const toDate = (value) => (value ? String(value).slice(0, 10) : '');
    const toNum = (value) => Number.parseFloat(value || 0);

    return [
      String(form.sachkonto_id || '') !== String(original.sachkonto_id || ''),
      String(form.liquiditaetskonto_id || '') !== String(original.liquiditaetskonto_id || ''),
      toNum(form.buchungsbetrag) !== toNum(original.buchungsbetrag),
      toDate(form.buchungsdatum) !== toDate(original.buchungsdatum),
      toNum(form.umsatzsteuersatz) !== toNum(original.umsatzsteuersatz),
      toNum(form.nettobetrag) !== toNum(original.nettobetrag),
      toNum(form.steuerbetrag) !== toNum(original.steuerbetrag)
    ].some(Boolean);
  };

  const navigateDirectlyTo3Stapel = async (buchung) => {
    if (!buchung?.buchungsid) {
      setSnackbar({ open: true, message: 'Buchung für Korrektur fehlt.', severity: 'error' });
      return;
    }

    const parseNumberOrNull = (value) => {
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    let bewegungsid = detectBewegungsId(buchung);
    let siblingActiveSplits = null;

    try {
      const context = await apiService.get(`/api/buchung/${buchung.buchungsid}/storno-kontext`);

      if (!bewegungsid) {
        const contextBewegungsid = parseInt(context?.bewegungsid, 10);
        if (Number.isInteger(contextBewegungsid) && contextBewegungsid > 0) {
          bewegungsid = contextBewegungsid;
        }
      }

      if (Array.isArray(context?.related_buchungen) && context.related_buchungen.length > 1) {
        const active = context.related_buchungen.filter(
          (r) => !['STORNIERT', 'KORRIGIERT'].includes(String(r.status || '').toUpperCase())
        );
        if (active.length > 1) {
          siblingActiveSplits = active.map((s) => ({
            buchungsid: s.buchungsid,
            sachkonto_id: s.sachkonto_id || null,
            buchungsbetrag: parseNumberOrNull(s.buchungsbetrag),
            objektid: s.objektid || null,
            wohnungsid: s.wohnungsid || null,
            umsatzsteuersatz: s.umsatzsteuersatz ?? null,
            nettobetrag: parseNumberOrNull(s.nettobetrag),
            steuerbetrag: parseNumberOrNull(s.steuerbetrag),
            belegid: s.belegid || null,
            notiz: s.notiz || ''
          }));
        }
      }
    } catch (contextErr) {
      console.warn('Konnte Bewegungs-ID / Sibling-Splits nicht laden:', contextErr);
    }

    if (!bewegungsid) {
      setSnackbar({
        open: true,
        message: 'Keine Kontobewegungs-ID gefunden. Korrektur über 3-Stapel ist für diese Buchung nicht möglich.',
        severity: 'error'
      });
      return;
    }

    const params = new URLSearchParams({
      vorschlag: '1',
      bewegungsid: String(bewegungsid)
    });

    navigate(`/dashboard/belegverarbeitung?${params.toString()}`, {
      state: {
        correctionPrefill: {
          sourceBuchungsId: buchung.buchungsid,
          neue_buchung: {
            sachkonto_id: buchung.sachkonto_id || null,
            liquiditaetskonto_id: buchung.liquiditaetskonto_id || null,
            buchungsbetrag: parseNumberOrNull(buchung.buchungsbetrag),
            buchungsdatum: buchung.buchungsdatum ? String(buchung.buchungsdatum).slice(0, 10) : null,
            notiz: buchung.notiz || null,
            objektid: buchung.objektid || null,
            wohnungsid: buchung.wohnungsid || null,
            belegid: buchung.belegid || null,
            umsatzsteuersatz: parseNumberOrNull(buchung.umsatzsteuersatz),
            nettobetrag: parseNumberOrNull(buchung.nettobetrag),
            steuerbetrag: parseNumberOrNull(buchung.steuerbetrag),
            bewegungsid
          },
          ...(siblingActiveSplits && { splits: siblingActiveSplits })
        }
      }
    });

    setEditDialog({ open: false, buchung: null });
    setSnackbar({ open: true, message: '3-Stapel-Verarbeitung zur Korrektur geöffnet.', severity: 'info' });
  };

  const clearFilters = () => {
    setFilters({
      year: '',
      objektid: '',
      buchungskontoid: '',
      buchungsart: '',
      wohnungsid: '',
      umsatzsteuersatz: '', // 🆕 NEU
      belegstatus: '',
      zuordnungsstatus: '',
      betragVon: '', // 🆕 NEU
      betragBis: ''  // 🆕 NEU
    });
    setShowFilters(false);
    setActiveTab(0);
  };

  const handleEditSave = async () => {
    const currentBooking = editDialog.buchung;
    if (!currentBooking) return;

    if (hasCriticalEditChanges(currentBooking, editForm)) {
      navigateDirectlyTo3Stapel(currentBooking);
      return;
    }

    try {
      await apiService.put(`/api/buchung/${currentBooking.buchungsid}`, {
        notiz: editForm.notiz,
        objektid: editForm.objektid || null,
        wohnungsid: editForm.wohnungsid || null
      });
      setSnackbar({ open: true, message: 'Meta-Daten erfolgreich aktualisiert', severity: 'success' });
      setEditDialog({ open: false, buchung: null });
      fetchBookings({ reset: true, nextPage: 0 });
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Buchung:', error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || error?.response?.data?.error || 'Fehler beim Aktualisieren der Buchung',
        severity: 'error'
      });
    }
  };



  const handleDeleteClick = async (buchung) => {
    const bewegungsid = detectBewegungsId(buchung);

    setDeleteDialog({
      open: true,
      buchung,
      bewegungsid,
      stornoAlleSplits: Boolean(bewegungsid),
      relatedActiveCount: 1,
      loadingContext: true
    });

    try {
      const context = await apiService.get(`/api/buchung/${buchung?.buchungsid}/storno-kontext`);
      const contextBewegungsid = parseInt(context?.bewegungsid, 10);
      const relatedActiveCount = parseInt(context?.active_buchungen_count, 10);
      const hasContextBewegungsid = Number.isInteger(contextBewegungsid) && contextBewegungsid > 0;
      const safeRelatedCount = Number.isInteger(relatedActiveCount) && relatedActiveCount > 0 ? relatedActiveCount : 1;
      const selectedAlreadyStorniert = String(buchung?.status || '').toUpperCase() === 'STORNIERT';
      const shouldPreferBatch = hasContextBewegungsid && (
        safeRelatedCount > 1 || (selectedAlreadyStorniert && safeRelatedCount >= 1)
      );

      setDeleteDialog((prev) => ({
        ...prev,
        bewegungsid: hasContextBewegungsid ? contextBewegungsid : prev.bewegungsid,
        relatedActiveCount: safeRelatedCount,
        stornoAlleSplits: shouldPreferBatch,
        loadingContext: false
      }));
    } catch (error) {
      console.warn('Storno-Kontext konnte nicht geladen werden, nutze lokale Erkennung:', error);
      setDeleteDialog((prev) => ({
        ...prev,
        loadingContext: false,
        relatedActiveCount: prev.bewegungsid ? 2 : 1,
        stornoAlleSplits: Boolean(prev.bewegungsid)
      }));
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      if (deleteDialog.stornoAlleSplits && deleteDialog.bewegungsid) {
        const result = await apiService.post('/api/buchung/storno-kontobewegung', {
          bewegungsid: deleteDialog.bewegungsid,
          storno_grund: 'Über Buchungsübersicht (Sammel-Storno) storniert'
        });

        setSnackbar({
          open: true,
          message: `Sammel-Storno abgeschlossen (${result?.storniert_count || 0} Buchung(en) storniert)`,
          severity: 'success'
        });
      } else {
        await apiService.post(`/api/buchung/${deleteDialog.buchung.buchungsid}/storno`, {
          storno_grund: 'Über Buchungsübersicht storniert'
        });

        setSnackbar({
          open: true,
          message: 'Buchung erfolgreich storniert und Zuordnungen rückgängig gemacht',
          severity: 'success'
        });
      }

      setDeleteDialog({ open: false, buchung: null, bewegungsid: null, stornoAlleSplits: false, relatedActiveCount: 1, loadingContext: false });
      fetchBookings({ reset: true, nextPage: 0 });
    } catch (error) {
      setSnackbar({ 
        open: true,
        message: 'Storno fehlgeschlagen – versuche Lösch-Fallback',
        severity: 'warning'
      });

      try {
        await apiService.delete(`/api/buchung/${deleteDialog.buchung.buchungsid}`);
        setSnackbar({ 
          open: true, 
          message: 'Buchung gelöscht (Fallback), Zuordnungen wurden zurückgesetzt', 
          severity: 'warning' 
        });
        setDeleteDialog({ open: false, buchung: null, bewegungsid: null, stornoAlleSplits: false, relatedActiveCount: 1, loadingContext: false });
        fetchBookings({ reset: true, nextPage: 0 });
      } catch (fallbackError) {
        console.error('Fehler beim Stornieren/Löschen der Buchung:', fallbackError);
        setSnackbar({ open: true, message: 'Fehler beim Stornieren/Löschen der Buchung', severity: 'error' });
      }
    }
  };

  const handleZuordnungAufheben = async () => {
    try {
      await apiService.post(
        `/api/buchung/${zuordnungsDialog.buchung?.buchungsid}/zuordnung-aufheben`,
        { grund: 'Korrigiert über UI' }
      );
      setSnackbar({
        open: true,
        message: 'Zuordnung(en) aufgehoben. Forderung(en) sind wieder offen.',
        severity: 'success'
      });
      setZuordnungsDialog({ open: false, buchung: null, neueForderungId: null });
      fetchBookings({ reset: true, nextPage: 0 });
    } catch (err) {
      console.error('Fehler beim Aufheben der Zuordnung:', err);
      setSnackbar({ open: true, message: 'Fehler beim Aufheben der Zuordnung', severity: 'error' });
    }
  };

  const handleNeuZuordnen = async () => {
    if (!zuordnungsDialog.neueForderungId) {
      setSnackbar({ open: true, message: 'Bitte eine Forderung auswählen', severity: 'warning' });
      return;
    }

    try {
      const betrag = Math.abs(parseFloat(zuordnungsDialog.buchung?.buchungsbetrag || 0));
      await apiService.post(
        `/api/buchung/forderung-ausgleichen`,
        {
          buchungskontoid: zuordnungsDialog.buchung?.sachkonto_id,
          buchungsbetrag: betrag,
          buchungsdatum: zuordnungsDialog.buchung?.buchungsdatum?.split('T')[0],
          forderungsid: zuordnungsDialog.neueForderungId
        }
      );
      setSnackbar({
        open: true,
        message: 'Buchung erfolgreich neu zugeordnet',
        severity: 'success'
      });
      setZuordnungsDialog({ open: false, buchung: null, neueForderungId: null });
      fetchBookings({ reset: true, nextPage: 0 });
    } catch (err) {
      console.error('Fehler beim Neu-Zuordnen:', err);
      setSnackbar({ open: true, message: 'Fehler beim Neu-Zuordnen', severity: 'error' });
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedBuchungsIds(new Set(bookings.map((b) => b.buchungsid)));
    } else {
      setSelectedBuchungsIds(new Set());
    }
  };

  const handleSelectOne = (buchungsid) => {
    setSelectedBuchungsIds((prev) => {
      const next = new Set(prev);
      if (next.has(buchungsid)) {
        next.delete(buchungsid);
      } else {
        next.add(buchungsid);
      }
      return next;
    });
  };

  const openBatchStornoDialog = async () => {
    const requestedIds = [...selectedBuchungsIds];
    if (requestedIds.length === 0) return;

    setBatchStornoDialog({
      open: true,
      loading: true,
      requestedIds,
      candidateBuchungen: [],
      selectedIds: [],
      errors: []
    });

    try {
      const contexts = await Promise.all(
        requestedIds.map(async (id) => {
          try {
            const ctx = await apiService.get(`/api/buchung/${id}/storno-kontext`);
            return { id, ctx };
          } catch (err) {
            return { id, err };
          }
        })
      );

      const bookingById = new Map(allBookings.map((b) => [b.buchungsid, b]));
      const candidateMap = new Map();
      const errors = [];

      contexts.forEach(({ id, ctx, err }) => {
        if (err) {
          errors.push(`Kontext für Buchung #${id} konnte nicht geladen werden`);
          const fallback = bookingById.get(id);
          if (fallback) {
            candidateMap.set(id, {
              buchungsid: id,
              status: String(fallback.status || 'AKTIV').toUpperCase(),
              buchungsbetrag: parseFloat(fallback.buchungsbetrag || 0),
              notiz: fallback.notiz || ''
            });
          }
          return;
        }

        const related = Array.isArray(ctx?.related_buchungen) ? ctx.related_buchungen : [];
        if (related.length === 0) {
          const fallback = bookingById.get(id);
          if (fallback) {
            candidateMap.set(id, {
              buchungsid: id,
              status: String(fallback.status || 'AKTIV').toUpperCase(),
              buchungsbetrag: parseFloat(fallback.buchungsbetrag || 0),
              notiz: fallback.notiz || ''
            });
          }
          return;
        }

        related.forEach((item) => {
          const bid = parseInt(item?.buchungsid, 10);
          if (!Number.isInteger(bid) || bid <= 0) return;
          if (!candidateMap.has(bid)) {
            candidateMap.set(bid, {
              buchungsid: bid,
              status: String(item?.status || 'AKTIV').toUpperCase(),
              buchungsbetrag: parseFloat(item?.buchungsbetrag || 0),
              notiz: item?.notiz || ''
            });
          }
        });
      });

      const candidateBuchungen = [...candidateMap.values()].sort((a, b) => a.buchungsid - b.buchungsid);
      const selectedIds = candidateBuchungen
        .filter((b) => !['STORNIERT', 'KORRIGIERT'].includes(String(b.status || '').toUpperCase()))
        .map((b) => b.buchungsid);

      setBatchStornoDialog({
        open: true,
        loading: false,
        requestedIds,
        candidateBuchungen,
        selectedIds,
        errors
      });
    } catch (err) {
      console.error('Fehler beim Vorladen des Batch-Storno-Kontexts:', err);
      setBatchStornoDialog({
        open: true,
        loading: false,
        requestedIds,
        candidateBuchungen: requestedIds.map((id) => ({
          buchungsid: id,
          status: 'AKTIV',
          buchungsbetrag: 0,
          notiz: ''
        })),
        selectedIds: requestedIds,
        errors: ['Kontext konnte nicht vollständig geladen werden.']
      });
    }
  };

  const toggleBatchCandidate = (buchungsid) => {
    setBatchStornoDialog((prev) => {
      const selected = new Set(prev.selectedIds || []);
      if (selected.has(buchungsid)) {
        selected.delete(buchungsid);
      } else {
        selected.add(buchungsid);
      }
      return { ...prev, selectedIds: [...selected] };
    });
  };

  const handleBatchStornoConfirm = async () => {
    try {
      const buchungsids = Array.isArray(batchStornoDialog.selectedIds)
        ? batchStornoDialog.selectedIds
        : [...selectedBuchungsIds];
      if (buchungsids.length === 0) {
        setSnackbar({ open: true, message: 'Bitte mindestens eine Buchung zum Storno auswählen', severity: 'warning' });
        return;
      }
      const result = await apiService.post('/api/buchung/storno-batch', {
        buchungsids,
        storno_grund: 'Batch-Storno über Buchungsübersicht'
      });
      setSnackbar({
        open: true,
        message: `Batch-Storno: ${result?.storniert_count || 0} Buchung(en) storniert${result?.bereits_storniert_count > 0 ? `, ${result.bereits_storniert_count} bereits storniert` : ''}`,
        severity: 'success'
      });
      setSelectedBuchungsIds(new Set());
      setBatchStornoDialog({ open: false });
      fetchBookings({ reset: true, nextPage: 0 });
    } catch (err) {
      console.error('Fehler beim Batch-Storno:', err);
      setSnackbar({ open: true, message: 'Fehler beim Batch-Storno', severity: 'error' });
    }
  };

  // Tab Panel Component
  function TabPanel({ children, value, index, ...other }) {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`booking-tabpanel-${index}`}
        aria-labelledby={`booking-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
      </div>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box my={4}>
        <Typography variant="h4" gutterBottom>
          📊 Erweiterte Buchungsübersicht
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          Alle Buchungen mit erweiterten Filtermöglichkeiten und Zuordnungsdetails
        </Typography>

        {/* 🆕 NEU: Spalten-Toggle */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <ViewColumnIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Spalten anzeigen
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={columnVisibility.objekt} 
                      onChange={(e) => setColumnVisibility({...columnVisibility, objekt: e.target.checked})} 
                    />
                  }
                  label="Objekt Details"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={columnVisibility.steuer} 
                      onChange={(e) => setColumnVisibility({...columnVisibility, steuer: e.target.checked})} 
                    />
                  }
                  label="Steuer-Details"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={columnVisibility.extended} 
                      onChange={(e) => setColumnVisibility({...columnVisibility, extended: e.target.checked})} 
                    />
                  }
                  label="Erweiterte Infos"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Filter-Bereiche */}
        <Paper sx={{ mb: 3 }}>
          {idVorauswahl && (
            <Alert
              severity="info"
              sx={{ m: 2, mb: 0 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setIdVorauswahl(null);
                    navigate(location.pathname, { replace: true });
                  }}
                >
                  Filter aufheben
                </Button>
              }
            >
              {idVorauswahl.length} Buchung(en) aus Vertragslöschung vorgefiltert und markiert — bitte über die Auswahl stornieren, danach kann der Vertrag gelöscht werden.
            </Alert>
          )}
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              label="Freitextsuche"
              placeholder="z.B. Belegname, Objekt, Konto, Notiz"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Box>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => {
              setActiveTab(newValue);
              if (newValue === 0) {
                setShowFilters(false);
                clearFilters();
              } else {
                setShowFilters(true);
              }
            }}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Alle Buchungen" />
            <Tab label="Nach Objekt" />
            <Tab label="Nach Beleg-Status" />
            <Tab label="Nach Zuordnung" />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Alle Buchungen ({totalCount ?? allBookings.length})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {totalCount ? `Geladen: ${allBookings.length} von ${totalCount}` : 'Geladene Buchungen'}
              </Typography>
            </Box>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Objekt auswählen</InputLabel>
                    <Select
                      value={filters.objektid}
                      onChange={(e) => setFilters({ ...filters, objektid: e.target.value })}
                      label="Objekt auswählen"
                    >
                      <MenuItem value="">Alle Objekte</MenuItem>
                      {objekte.map((objekt) => (
                        <MenuItem key={objekt.objektid} value={objekt.objektid}>
                          {objekt.objektkuerzel} - {objekt.objektstrasse} {objekt.objekthausnummer}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Wohneinheit auswählen</InputLabel>
                    <Select
                      value={filters.wohnungsid}
                      onChange={(e) => setFilters({ ...filters, wohnungsid: e.target.value })}
                      label="Wohneinheit auswählen"
                    >
                      <MenuItem value="">Alle Wohneinheiten</MenuItem>
                      {wohneinheiten
                        .filter(wohnung => !filters.objektid || wohnung.objektid === parseInt(filters.objektid))
                        .map((wohnung) => (
                          <MenuItem key={wohnung.wohnungsid} value={wohnung.wohnungsid}>
                            {wohnung.wohnungskuerzel} - {wohnung.wohnungsbezeichnung}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Beleg-Status</InputLabel>
                    <Select
                      value={filters.belegstatus}
                      onChange={(e) => setFilters({ ...filters, belegstatus: e.target.value })}
                      label="Beleg-Status"
                    >
                      <MenuItem value="">Alle</MenuItem>
                      <MenuItem value="mit_beleg">Mit Beleg</MenuItem>
                      <MenuItem value="ohne_beleg">Ohne Beleg</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Zuordnungs-Status</InputLabel>
                    <Select
                      value={filters.zuordnungsstatus}
                      onChange={(e) => setFilters({ ...filters, zuordnungsstatus: e.target.value })}
                      label="Zuordnungs-Status"
                    >
                      <MenuItem value="">Alle</MenuItem>
                      <MenuItem value="vollstaendig">Vollständig zugeordnet</MenuItem>
                      <MenuItem value="unzugeordnet">Unzugeordnet</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={clearFilters}
                    fullWidth
                  >
                    Filter zurücksetzen
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
        </Paper>

        {/* Mehrfachauswahl-Toolbar */}
        {selectedBuchungsIds.size > 0 && (
          <Paper sx={{ mb: 2, p: 2, backgroundColor: '#e3f2fd', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
              {selectedBuchungsIds.size} Buchung{selectedBuchungsIds.size !== 1 ? 'en' : ''} ausgewählt
            </Typography>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={openBatchStornoDialog}
            >
              Ausgewählte stornieren
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ClearIcon />}
              onClick={() => setSelectedBuchungsIds(new Set())}
            >
              Auswahl aufheben
            </Button>
          </Paper>
        )}

        {/* Haupttabelle */}
        <TableContainer 
          component={Paper} 
          sx={{ 
            overflowX: 'auto',
            width: '100%',
            '& .MuiTable-root': {
              minWidth: '1400px',
              width: '100%'
            }
          }}
        >
          <Table size="small" sx={{ tableLayout: 'auto', width: '100%' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={selectedBuchungsIds.size > 0 && selectedBuchungsIds.size < bookings.length}
                    checked={bookings.length > 0 && selectedBuchungsIds.size === bookings.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: '80px' }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: '200px' }}>Sachkonto</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: '180px' }}>Liquiditätskonto</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: '120px' }}>Betrag</TableCell>
                
                {/* Objekt-Info Spalten */}
                {columnVisibility.objekt && (
                  <>
                    <TableCell sx={{ fontWeight: 600, minWidth: '80px', borderLeft: '2px solid #e0e0e0' }}>Objekt</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: '100px' }}>Wohneinheit</TableCell>
                  </>
                )}
                
                {/* Steuer-Info Spalten */}
                {columnVisibility.steuer && (
                  <>
                    <TableCell sx={{ fontWeight: 600, minWidth: '80px', borderLeft: '2px solid #e0e0e0' }}>USt-Satz</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: '100px' }}>Netto €</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: '100px' }}>Steuer €</TableCell>
                  </>
                )}
                
                {/* Erweiterte Info Spalten */}
                {columnVisibility.extended && (
                  <>
                    <TableCell sx={{ fontWeight: 600, minWidth: '150px', borderLeft: '2px solid #e0e0e0' }}>Buchungstext</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: '120px' }}>Zuordnungen</TableCell>
                  </>
                )}
                
                {/* Basis Spalten */}
                <TableCell sx={{ fontWeight: 600, minWidth: '130px', borderLeft: '2px solid #e0e0e0' }}>Art</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: '100px' }}>Datum</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: '180px' }}>Beleg/Dokument</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: '150px' }}>Notiz</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: '100px' }}>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={17} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {showFilters ? 'Keine Buchungen gefunden' : 'Noch keine Buchungen vorhanden'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {showFilters ? 'Versuche andere Filter-Einstellungen' : 'Erstelle Mietforderungen oder lade Belege hoch'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                bookings.map((booking) => (
                  <TableRow
                    key={booking.buchungsid}
                    hover
                    selected={selectedBuchungsIds.has(booking.buchungsid)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedBuchungsIds.has(booking.buchungsid)}
                        onChange={() => handleSelectOne(booking.buchungsid)}
                      />
                    </TableCell>
                    {/* ID */}
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      #{booking.buchungsid}
                    </TableCell>
                    
                    {/* Sachkonto */}
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                          {booking.sachkonto_nummer || booking.buchungskonto || '-'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {booking.sachkonto_bezeichnung || booking.buchungskontokategorie || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    {/* Liquiditätskonto */}
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                          {booking.liquiditaetskonto_nummer || '-'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {booking.liquiditaetskonto_bezeichnung || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    {/* Betrag */}
                    <TableCell sx={{ 
                      fontWeight: 600,
                      color: parseFloat(booking.buchungsbetrag) < 0 ? '#d32f2f' : '#2e7d32',
                      textAlign: 'right'
                    }}>
                      {formatCurrency(booking.buchungsbetrag)}
                    </TableCell>
                    
                    {/* Objekt-Info Spalten */}
                    {columnVisibility.objekt && (
                      <>
                        <TableCell sx={{ textAlign: 'center' }}>
                          {booking.objektkuerzel ? (
                            <Chip label={booking.objektkuerzel} size="small" variant="outlined" />
                          ) : (
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>-</Typography>
                          )}
                        </TableCell>
                        
                        <TableCell>
                          {booking.wohnungskuerzel ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                                {booking.wohnungskuerzel}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Wohneinheit
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>-</Typography>
                          )}
                        </TableCell>
                      </>
                    )}
                    
                    {/* Steuer-Info Spalten */}
                    {columnVisibility.steuer && (
                      <>
                        <TableCell sx={{ textAlign: 'center' }}>
                          {booking.umsatzsteuersatz ? (
                            <Chip 
                              label={`${booking.umsatzsteuersatz}%`} 
                              size="small" 
                              color={booking.umsatzsteuersatz === '19' ? 'primary' : 'default'}
                            />
                          ) : (
                            <Chip label="0%" size="small" color="default" />
                          )}
                        </TableCell>
                        
                        <TableCell sx={{ textAlign: 'right' }}>
                          {booking.nettobetrag ? (
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {formatCurrency(booking.nettobetrag)}
                            </Typography>
                          ) : (
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>-</Typography>
                          )}
                        </TableCell>
                        
                        <TableCell sx={{ textAlign: 'right' }}>
                          {booking.steuerbetrag ? (
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>
                              {formatCurrency(booking.steuerbetrag)}
                            </Typography>
                          ) : (
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>-</Typography>
                          )}
                        </TableCell>
                      </>
                    )}
                    
                    {/* Erweiterte Info Spalten */}
                    {columnVisibility.extended && (
                      <>
                        <TableCell>
                          {booking.buchungstext ? (
                            <Typography variant="caption" sx={{ 
                              color: 'text.secondary',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block'
                            }}>
                              {booking.buchungstext.length > 25 ? 
                                booking.buchungstext.substring(0, 25) + '...' : 
                                booking.buchungstext}
                            </Typography>
                          ) : (
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>-</Typography>
                          )}
                        </TableCell>
                        
                        <TableCell>
                          {(booking.anzahl_zuordnungen || 0) > 0 ? (
                            <Box>
                              <Chip 
                                label={`${booking.anzahl_zuordnungen} Zuordnung${booking.anzahl_zuordnungen > 1 ? 'en' : ''}`}
                                size="small" 
                                color="success"
                                onClick={() => setZuordnungsDialog({ open: true, buchung: booking })}
                                sx={{ cursor: 'pointer', mb: 0.5 }}
                              />
                              <Typography variant="caption" display="block" color="text.secondary">
                                {formatCurrency(booking.zugeordneter_gesamtbetrag || 0)}
                              </Typography>
                            </Box>
                          ) : (
                            <Chip label="Unzugeordnet" size="small" color="default" />
                          )}
                        </TableCell>
                      </>
                    )}
                    
                    {/* Art */}
                    <TableCell>
                      <Chip
                        label={booking.buchungsart || 'zahlung'}
                        size="small"
                        sx={{ 
                          fontSize: '0.75rem',
                          backgroundColor: booking.automatisch_generiert ? '#e3f2fd' : '#f3e5f5',
                          color: booking.automatisch_generiert ? '#1976d2' : '#7b1fa2'
                        }}
                      />
                      {booking.automatisch_generiert && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
                          automatisch
                        </Typography>
                      )}
                    </TableCell>
                    
                    {/* Datum */}
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {formatDate(booking.buchungsdatum)}
                    </TableCell>
                    
                    {/* Beleg/Dokument */}
                    <TableCell>
                      {booking.belegid ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                            Beleg #{booking.belegid}
                          </Typography>
                          {booking.belegname && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>
                              {booking.belegname.length > 25 ? booking.belegname.substring(0, 25) + '...' : booking.belegname}
                            </Typography>
                          )}
                          {booking.cloudinary_url && (
                            <Tooltip title="Beleg öffnen">
                              <IconButton
                                size="small"
                                onClick={() => window.open(booking.cloudinary_url, '_blank')}
                                sx={{ color: 'primary.main', p: 0.25 }}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                          Kein Beleg
                        </Typography>
                      )}
                    </TableCell>
                    
                    {/* Notiz */}
                    <TableCell>
                      {booking.notiz ? (
                        <Typography variant="caption" sx={{ 
                          color: 'text.secondary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block'
                        }}>
                          {booking.notiz.length > 30 ? booking.notiz.substring(0, 30) + '...' : booking.notiz}
                        </Typography>
                      ) : (
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>-</Typography>
                      )}
                    </TableCell>
                    
                    {/* Aktionen */}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Buchung korrigieren">
                          <IconButton 
                            size="small" 
                            onClick={() => navigateDirectlyTo3Stapel(booking)}
                            sx={{ color: '#1976d2' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Buchung löschen">
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteClick(booking)}
                            sx={{ color: '#d32f2f' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => fetchBookings({ reset: false, nextPage: page + 1 })}
            disabled={!hasMore || isLoading}
          >
            {isLoading ? 'Lädt...' : hasMore ? 'Mehr laden' : 'Alles geladen'}
          </Button>
        </Box>
        
        {/* Statistik */}
        {bookings.length > 0 && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>📊 Statistik</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Typography variant="body2">
                  <strong>Gesamt Buchungen:</strong> {bookings.length}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2">
                  <strong>Mit Beleg:</strong> {bookings.filter(b => b.belegid).length}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2">
                  <strong>Zugeordnet:</strong> {bookings.filter(b => (b.anzahl_zuordnungen || 0) > 0).length}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2">
                  <strong>Gesamtbetrag:</strong> {formatCurrency(bookings.reduce((sum, b) => sum + parseFloat(b.buchungsbetrag), 0))}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, buchung: null })} maxWidth="md" fullWidth>
          <DialogTitle>Buchung bearbeiten</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {(editForm.status || editForm.source_typ) && (
                <Alert severity="info">
                  Status: {editForm.status || '-'} | Herkunft: {editForm.source_typ || '-'}
                  {editForm.source_id ? ` #${editForm.source_id}` : ''}
                </Alert>
              )}

              {correctionDetails.loading && (
                <Alert severity="info">Lade Korrekturkette...</Alert>
              )}

              {!correctionDetails.loading && correctionDetails.data && (
                <Alert severity="info">
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {correctionDetails.data.korrigiert_aus_buchung_id && (
                      <Chip
                        size="small"
                        color="warning"
                        label={`Korrigiert aus #${correctionDetails.data.korrigiert_aus_buchung_id}`}
                      />
                    )}
                    {correctionDetails.data.ersetzt_durch_buchung_id && (
                      <Chip
                        size="small"
                        color="success"
                        label={`Ersetzt durch #${correctionDetails.data.ersetzt_durch_buchung_id}`}
                      />
                    )}
                    {correctionDetails.data.korrektur_grund_code && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Grundcode: ${correctionDetails.data.korrektur_grund_code}`}
                      />
                    )}
                  </Box>
                  {correctionDetails.data.korrektur_grund && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Änderungsgrund: {correctionDetails.data.korrektur_grund}
                    </Typography>
                  )}
                </Alert>
              )}

              <FormControl fullWidth>
                <InputLabel>Sachkonto</InputLabel>
                <Select
                  value={editForm.sachkonto_id}
                  onChange={(e) => setEditForm({ ...editForm, sachkonto_id: e.target.value })}
                  label="Sachkonto"
                >
                  {konten.map((konto) => (
                    <MenuItem key={konto.konto_id} value={konto.konto_id}>
                      {konto.kontonummer} - {konto.kontobezeichnung}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Liquiditätskonto (optional)</InputLabel>
                <Select
                  value={editForm.liquiditaetskonto_id}
                  onChange={(e) => setEditForm({ ...editForm, liquiditaetskonto_id: e.target.value })}
                  label="Liquiditätskonto (optional)"
                >
                  <MenuItem value="">
                    <em>Kein Liquiditätskonto</em>
                  </MenuItem>
                  {liquiditaetskonten.map((konto) => (
                    <MenuItem key={konto.konto_id} value={konto.konto_id}>
                      {konto.kontonummer} - {konto.kontobezeichnung}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Buchungsbetrag"
                type="number"
                value={editForm.buchungsbetrag}
                onChange={(e) => setEditForm({ ...editForm, buchungsbetrag: e.target.value })}
                inputProps={{ step: 0.01 }}
              />

              <TextField
                fullWidth
                label="Buchungsdatum"
                type="date"
                value={editForm.buchungsdatum}
                onChange={(e) => setEditForm({ ...editForm, buchungsdatum: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />

              <FormControl fullWidth>
                <InputLabel>Objekt (optional)</InputLabel>
                <Select
                  value={editForm.objektid}
                  onChange={(e) => {
                    const neuesObjektId = e.target.value;
                    // Prüfe ob aktuelle Wohneinheit zum neuen Objekt gehört
                    const wohneinheitGehoertZuObjekt = wohneinheiten.find(
                      w => w.wohnungsid === editForm.wohnungsid && w.objektid === neuesObjektId
                    );
                    setEditForm({ 
                      ...editForm, 
                      objektid: neuesObjektId,
                      // Setze Wohneinheit zurück wenn sie nicht zum neuen Objekt gehört
                      wohnungsid: wohneinheitGehoertZuObjekt ? editForm.wohnungsid : ''
                    });
                  }}
                  label="Objekt (optional)"
                >
                  <MenuItem value="">Kein Objekt</MenuItem>
                  {objekte.map((objekt) => (
                    <MenuItem key={objekt.objektid} value={objekt.objektid}>
                      {objekt.objektkuerzel} - {objekt.objektstrasse} {objekt.objekthausnummer}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Wohneinheit (optional)</InputLabel>
                <Select
                  value={editForm.wohnungsid}
                  onChange={(e) => setEditForm({ ...editForm, wohnungsid: e.target.value })}
                  label="Wohneinheit (optional)"
                  disabled={!editForm.objektid}
                >
                  <MenuItem value="">Keine Wohneinheit</MenuItem>
                  {wohneinheiten
                    .filter(wohnung => !editForm.objektid || wohnung.objektid === editForm.objektid)
                    .map((wohnung) => (
                      <MenuItem key={wohnung.wohnungsid} value={wohnung.wohnungsid}>
                        {wohnung.wohnungskuerzel} - {wohnung.wohnungsbezeichnung}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Notiz"
                multiline
                rows={3}
                value={editForm.notiz}
                onChange={(e) => setEditForm({ ...editForm, notiz: e.target.value })}
              />

              <Alert severity="info">
                Wertrelevante Änderungen (Betrag, Konto, Datum, USt) werden aus Transparenzgründen nur über den Korrektur-Flow mit Änderungsgrund gespeichert.
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialog({ open: false, buchung: null })}>
              Abbrechen
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigateDirectlyTo3Stapel(editDialog.buchung)}
            >
              In 3-Stapel korrigieren
            </Button>
            <Button variant="contained" onClick={handleEditSave} startIcon={<SaveIcon />}>
              Nur Meta speichern
            </Button>
          </DialogActions>
        </Dialog>


        {/* Delete Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, buchung: null, bewegungsid: null, stornoAlleSplits: false, relatedActiveCount: 1, loadingContext: false })}
        >
          <DialogTitle>Buchung löschen</DialogTitle>
          <DialogContent>
            <Typography>
              Sind Sie sicher, dass Sie diese Buchung löschen möchten?
            </Typography>
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff3e0', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Buchung #{deleteDialog.buchung?.buchungsid}
              </Typography>
              <Typography variant="body2">
                Betrag: {formatCurrency(deleteDialog.buchung?.buchungsbetrag)}
              </Typography>
              <Typography variant="body2">
                Konto: {deleteDialog.buchung?.buchungskonto}
              </Typography>
              {deleteDialog.bewegungsid && (
                <Typography variant="body2">
                  Kontobewegung: #{deleteDialog.bewegungsid}
                </Typography>
              )}
            </Box>
            {deleteDialog.bewegungsid && (
              <FormControlLabel
                sx={{ mt: 2 }}
                control={(
                  <Switch
                    checked={deleteDialog.stornoAlleSplits}
                    disabled={deleteDialog.loadingContext || !deleteDialog.bewegungsid || (
                      deleteDialog.relatedActiveCount <= 1 &&
                      String(deleteDialog.buchung?.status || '').toUpperCase() !== 'STORNIERT'
                    )}
                    onChange={(e) => setDeleteDialog((prev) => ({
                      ...prev,
                      stornoAlleSplits: e.target.checked
                    }))}
                  />
                )}
                label="Alle Splits dieser Kontobewegung gemeinsam stornieren"
              />
            )}
            {deleteDialog.loadingContext && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Prüfe verknüpfte Positionen...
              </Alert>
            )}
            {!deleteDialog.loadingContext && deleteDialog.bewegungsid && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {deleteDialog.relatedActiveCount > 1
                  ? `Zu dieser Kontobewegung gehören aktuell ${deleteDialog.relatedActiveCount} aktive Positionen. Sammel-Storno ist vorausgewählt.`
                  : String(deleteDialog.buchung?.status || '').toUpperCase() === 'STORNIERT'
                    ? 'Diese Position ist bereits storniert, aber es existiert noch eine aktive Schwester-Position. Sammel-Storno ist vorausgewählt.'
                    : 'Keine weiteren aktiven Schwester-Positionen erkannt.'}
              </Alert>
            )}
            <Alert severity="warning" sx={{ mt: 2 }}>
              {deleteDialog.stornoAlleSplits && deleteDialog.bewegungsid
                ? 'Sammel-Storno: Alle zugehörigen Split-Buchungen werden storniert und Forderungs-Zuordnungen zurückgesetzt.'
                : 'Die Buchung wird primär storniert (revisionssicher). Falls nicht möglich, erfolgt ein Lösch-Fallback.'}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false, buchung: null, bewegungsid: null, stornoAlleSplits: false, relatedActiveCount: 1, loadingContext: false })}>
              Abbrechen
            </Button>
            <Button variant="contained" color="error" onClick={handleDeleteConfirm} startIcon={<DeleteIcon />} disabled={deleteDialog.loadingContext}>
              {deleteDialog.stornoAlleSplits && deleteDialog.bewegungsid ? 'Alle Splits stornieren' : 'Löschen'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Zuordnungs-Details Dialog */}
        <Dialog open={zuordnungsDialog.open} onClose={() => setZuordnungsDialog({ open: false, buchung: null, neueForderungId: null })} maxWidth="md" fullWidth>
          <DialogTitle>
            Zuordnungen für Buchung #{zuordnungsDialog.buchung?.buchungsid} (Korrigieren)
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Alert severity="info">
                Betrag: <strong>{formatCurrency(zuordnungsDialog.buchung?.buchungsbetrag || 0)}</strong>
              </Alert>

              <Typography variant="body2" color="text.secondary">
                Diese Buchung ist mit folgenden Kontobewegungen verknüpft:
              </Typography>
              {zuordnungsDialog.buchung?.verwendungszwecke && zuordnungsDialog.buchung?.verwendungszwecke.length > 0 ? (
                <Box>
                  {zuordnungsDialog.buchung.verwendungszwecke.map((zweck, index) => (
                    <Chip key={index} label={zweck} sx={{ mr: 1, mb: 1 }} />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Keine Zuordnungsdetails verfügbar
                </Typography>
              )}

              <Alert severity="warning">
                Wenn Sie hier auf „Aufheben“ klicken, wird die Zuordnung gelöst und die Forderung wieder als offen markiert.
              </Alert>

              {zuordnungsDialog.buchung?.verwendungszwecke && zuordnungsDialog.buchung?.verwendungszwecke.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 2 }}>Neu zuordnen</Typography>
                  <FormControl fullWidth>
                    <InputLabel>Forderung auswählen</InputLabel>
                    <Select
                      value={zuordnungsDialog.neueForderungId || ''}
                      onChange={(e) => setZuordnungsDialog((prev) => ({ ...prev, neueForderungId: e.target.value }))}
                      label="Forderung auswählen"
                    >
                      <MenuItem value="">-- Bitte wählen --</MenuItem>
                      {forderungen.map((f) => (
                        <MenuItem key={f.forderung_id} value={f.forderung_id}>
                          Forderung #{f.forderung_id} – {formatCurrency(f.brutto_betrag || 0)} (
                          {f.status || 'OFFEN'}) | {f.mieter1_nachname || 'N/A'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setZuordnungsDialog({ open: false, buchung: null, neueForderungId: null })}>
              Abbrechen
            </Button>
            {zuordnungsDialog.buchung?.verwendungszwecke && zuordnungsDialog.buchung?.verwendungszwecke.length > 0 && (
              <>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleZuordnungAufheben}
                  startIcon={<ClearIcon />}
                >
                  Aufheben
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleNeuZuordnen}
                  disabled={!zuordnungsDialog.neueForderungId}
                  startIcon={<SaveIcon />}
                >
                  Neu zuordnen
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Batch-Storno Dialog */}
        <Dialog open={batchStornoDialog.open} onClose={() => setBatchStornoDialog({ open: false })} maxWidth="sm" fullWidth>
          <DialogTitle>Buchungen stornieren</DialogTitle>
          <DialogContent>
            <Typography>
              Möchten Sie die folgenden Buchungen unwiderruflich stornieren?
            </Typography>
            {batchStornoDialog.loading && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Zugehörige Splits werden geladen...
              </Alert>
            )}
            {!batchStornoDialog.loading && Array.isArray(batchStornoDialog.errors) && batchStornoDialog.errors.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {batchStornoDialog.errors.join(' | ')}
              </Alert>
            )}
            {!batchStornoDialog.loading && Array.isArray(batchStornoDialog.candidateBuchungen) && batchStornoDialog.candidateBuchungen.length > 0 && (
              <Box sx={{ mt: 2, maxHeight: 280, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                {batchStornoDialog.candidateBuchungen.map((item) => {
                  const isChecked = (batchStornoDialog.selectedIds || []).includes(item.buchungsid);
                  const isAlreadyStorniert = ['STORNIERT', 'KORRIGIERT'].includes(String(item.status || '').toUpperCase());
                  return (
                    <Box key={item.buchungsid} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                      <FormControlLabel
                        control={<Checkbox checked={isChecked} onChange={() => toggleBatchCandidate(item.buchungsid)} disabled={isAlreadyStorniert} />}
                        label={`#${item.buchungsid} • ${formatCurrency(item.buchungsbetrag || 0)}`}
                        sx={{ mr: 1 }}
                      />
                      <Chip
                        size="small"
                        label={isAlreadyStorniert ? item.status : 'AKTIV'}
                        color={isAlreadyStorniert ? 'default' : 'success'}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}
            <Alert severity="warning" sx={{ mt: 2 }}>
              Alle zugehörigen Forderungs-Zuordnungen werden zurückgesetzt. Die Forderungen erscheinen wieder als offen und die Kontobewegungen als unverarbeitet.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBatchStornoDialog({ open: false })}>Abbrechen</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleBatchStornoConfirm}
              startIcon={<DeleteIcon />}
              disabled={batchStornoDialog.loading || !Array.isArray(batchStornoDialog.selectedIds) || batchStornoDialog.selectedIds.length === 0}
            >
              {(Array.isArray(batchStornoDialog.selectedIds) ? batchStornoDialog.selectedIds.length : selectedBuchungsIds.size)} Buchung{(Array.isArray(batchStornoDialog.selectedIds) ? batchStornoDialog.selectedIds.length : selectedBuchungsIds.size) !== 1 ? 'en' : ''} stornieren
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
}

export default BookingTableEnhanced;
